import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useMemo } from 'react'

import { errorView, useDebouncedValue } from '@krp/client-runtime'
import { SEARCH_DEBOUNCE_MS, SEARCH_PAGE_SIZE, type PickerRowVm, type PickerVm, type SearchVm } from '@krp/sale-ui'

import { ownPriceTypes } from '../../connection-form/api/connectionApi'
import type { OwnDispatch } from '../../../state'
import type { SaleProduct } from '../../sale/model/cart'
import type { OwnCatalog, OwnLookup } from '../../sale/model/useOwnSale'
import { ownSaleLines, ownSaleSettings } from '../api/linesApi'
import type { OwnSaleLines } from '../api/linesContracts'
import { ownSaleSearch } from '../api/searchApi'
import type { OwnSearchItem, OwnSearchPage } from '../api/searchContracts'
import { pickerRowOf, type OwnSaleLine } from './pickerRows'
import { saleLineOf, saleProductOf } from './saleLine'

/**
 * Каталог источника «Собственная»: поиск по общему каталогу Platform и «строки для продажи» службы Resource.Own (цена выбранного вида
 * цены и ставка НДС товара) — оба запроса под правом `session.open` на ресурсе «Собственная», прав на каталог и цены у кассира нет.
 * Найденное показывается всегда; в чек попадает только то, у чего есть цена и ставка, а остальное выключено с причиной словами.
 */

/** Вид цены не выбран и по умолчанию не задан: кассиру сказано, куда идти. */
export const NO_PRICE_TYPE_REASON = 'Не выбран вид цены: «Действия» → «Вид цены»'

const EMPTY_PICKER: PickerVm = {
  query: { status: 'idle', refreshing: false, error: null },
  search: '',
  rows: [],
  page: 1,
  canPrev: false,
  canNext: false,
  totalText: '',
}

export interface SearchState {
  /** Что набрано сейчас (без задержки ввода). */
  typed: string
  /** Что реально ушло в запрос (после задержки ввода). */
  sent: string
  page: OwnSearchPage | undefined
  /** Поиск или строки для продажи ещё в пути. */
  loading: boolean
  errorText: string | null
  /** Вид цены не выбран: строки выключены с этой причиной, а не пустой ценой. */
  noPriceType?: boolean
}

/** Строки выдачи и состояние поиска для экрана. Чистая функция: без сети. */
export function searchVmOf(state: SearchState, lines: ReadonlyMap<string, OwnSaleLine>): SearchVm {
  const typed = state.typed.trim()
  if (typed === '') return { text: state.typed, rows: [], status: 'idle', errorText: null }
  if (state.errorText !== null) return { text: state.typed, rows: [], status: 'error', errorText: state.errorText }
  // Пауза ввода или ответ ещё не пришёл: выдача прошлого текста не показывается как ответ на новый.
  if (state.loading || state.page === undefined || typed !== state.sent) return { text: state.typed, rows: [], status: 'waiting', errorText: null }
  const rows: readonly PickerRowVm[] = state.page.items.map((item) => {
    if (item.cardKey === null) return pickerRowOf(item, undefined)
    if (state.noPriceType === true) {
      return pickerRowOf(item, { productKey: item.cardKey, priceText: '—', unit: null, isWeight: null, blockedReason: NO_PRICE_TYPE_REASON })
    }
    return pickerRowOf(item, lines.get(item.cardKey))
  })
  return { text: state.typed, rows, status: 'ready', errorText: null }
}

export interface LinesView {
  lines: ReadonlyMap<string, OwnSaleLine>
  products: ReadonlyMap<string, SaleProduct>
}

const NO_LINES: LinesView = { lines: new Map(), products: new Map() }

/** Строки для продажи по ключам карточек: что показать в выдаче и что положить в корзину. Чистая функция. */
export function linesViewOf(response: OwnSaleLines | undefined): LinesView {
  if (response === undefined) return NO_LINES
  const lines = new Map<string, OwnSaleLine>()
  const products = new Map<string, SaleProduct>()
  for (const item of response.items) {
    lines.set(item.cardKey, saleLineOf(item))
    const product = saleProductOf(item)
    if (product !== null) products.set(item.cardKey, product)
  }
  return { lines, products }
}

/** Ключи карточек из выдачи, которые есть в номенклатуре источника: без повторов, не больше предела запроса. */
export function cardKeysOf(page: OwnSearchPage | undefined): readonly string[] {
  if (page === undefined) return []
  const keys = page.items.flatMap((item) => (item.inCash && item.cardKey !== null ? [item.cardKey] : []))
  return [...new Set(keys)].slice(0, SEARCH_PAGE_SIZE)
}

/** Строка из одних цифр — штрихкод или артикул, а не наименование. */
const DIGITS = /^\d+$/
/** Точный запрос штрихкода: две записи, чтобы заметить, что код есть у нескольких товаров. */
const EXACT_LIMIT = 2

export type FoundItem = OwnSearchItem | 'not-found' | 'ambiguous'

/** Единственная запись или «несколько»/«нет». */
function onlyOf(items: readonly OwnSearchItem[]): FoundItem | null {
  if (items.length > 1) return 'ambiguous'
  return items[0] ?? null
}

/**
 * Запись для кода из цифр по текстовой выдаче (точный запрос штрихкода ничего не дал): только с тем же штрихкодом или артикулом целиком.
 * «Похожий» товар по коду не берётся никогда (К-117): склеенный или недочитанный код иначе клал бы в чек соседний товар.
 */
export function codeItemOf(text: string, items: readonly OwnSearchItem[]): FoundItem {
  return onlyOf(items.filter((item) => item.matchedBarcode === text)) ?? onlyOf(items.filter((item) => item.sku === text)) ?? 'not-found'
}

/** Запись для наименования: первый товар из базы источника, иначе первый найденный (он вернётся с причиной «нет в номенклатуре»). */
export function nameItemOf(items: readonly OwnSearchItem[]): FoundItem {
  return items.find((item) => item.inCash && item.cardKey !== null) ?? items[0] ?? 'not-found'
}

/** Товар ровно по отправленной строке (К-117): код из цифр — точный штрихкод, затем {@link codeItemOf}; наименование — {@link nameItemOf}. */
async function findItem(dispatch: OwnDispatch, resourceId: string, text: string): Promise<FoundItem> {
  const search = (mode: 'text' | 'barcode', limit: number) =>
    dispatch(ownSaleSearch.initiate({ resourceId, q: text, mode, limit }, { subscribe: false, forceRefetch: true })).unwrap()
  if (DIGITS.test(text)) {
    const exact = onlyOf((await search('barcode', EXACT_LIMIT)).items)
    return exact ?? codeItemOf(text, (await search('text', SEARCH_PAGE_SIZE)).items)
  }
  return nameItemOf((await search('text', SEARCH_PAGE_SIZE)).items)
}

export function useOwnCatalog(resourceId: string, searchText: string, priceTypeKey: string | null, dispatch: OwnDispatch): OwnCatalog {
  const sent = useDebouncedValue(searchText.trim(), SEARCH_DEBOUNCE_MS)
  const skip = resourceId === ''
  const settings = ownSaleSettings.useQuery(skip ? skipToken : { resourceId })
  const types = ownPriceTypes.useQuery(skip ? skipToken : { resourceId })
  const searchArgs = useMemo(() => (sent === '' || skip ? null : { resourceId, q: sent, limit: SEARCH_PAGE_SIZE }), [resourceId, sent, skip])
  const search = ownSaleSearch.useQuery(searchArgs ?? skipToken)

  const defaultKey = settings.data?.defaultPriceTypeKey ?? null
  const effectiveKey = priceTypeKey ?? defaultKey
  const cardKeys = useMemo(() => cardKeysOf(search.data), [search.data])
  const linesArgs = useMemo(
    () => (cardKeys.length === 0 || settings.data === undefined || effectiveKey === null ? null : { resourceId, cardKeys, priceTypeKey: effectiveKey }),
    [effectiveKey, cardKeys, resourceId, settings.data],
  )
  const linesQuery = ownSaleLines.useQuery(linesArgs ?? skipToken)
  const lines = useMemo(() => linesViewOf(linesArgs === null ? undefined : linesQuery.data), [linesArgs, linesQuery.data])

  // Каталог — сам ресурс: берутся виды цен, чей источник — этот ресурс.
  const priceTypes = useMemo(
    () => (types.data ?? []).filter((type) => type.sourceResourceId === resourceId).map((type) => ({ key: type.key, name: type.name })),
    [resourceId, types.data],
  )

  const errorText = search.isError ? errorView(search.error).message : linesQuery.isError ? errorView(linesQuery.error).message : null
  // Выдача ждёт и строки для продажи: без них цена на строке была бы пустой не потому, что её нет, а потому что она ещё в пути.
  const loading = search.isFetching || (linesArgs !== null && (linesQuery.isFetching || linesQuery.data === undefined) && !linesQuery.isError)
  const vm = useMemo(
    () => searchVmOf({ typed: searchText, sent, page: search.data, loading, errorText, noPriceType: settings.data !== undefined && effectiveKey === null }, lines.lines),
    [effectiveKey, errorText, lines.lines, loading, search.data, searchText, sent, settings.data],
  )
  // К-117: Enter (сканер) — товар ровно по отправленной строке, а не первая строка выдачи, которая по новому коду ещё не пришла.
  const lookup = useCallback(
    async (text: string): Promise<OwnLookup> => {
      try {
        const item = await findItem(dispatch, resourceId, text)
        if (item === 'not-found' || item === 'ambiguous') return { kind: item }
        if (!item.inCash || item.cardKey === null) return { kind: 'row', row: pickerRowOf(item, undefined), product: null }
        const key =
          priceTypeKey ??
          (await dispatch(ownSaleSettings.initiate({ resourceId }, { subscribe: false })).unwrap()).defaultPriceTypeKey ??
          null
        if (key === null) {
          const blocked = { productKey: item.cardKey, priceText: '—', unit: null, isWeight: null, blockedReason: NO_PRICE_TYPE_REASON }
          return { kind: 'row', row: pickerRowOf(item, blocked), product: null }
        }
        const found = linesViewOf(
          await dispatch(ownSaleLines.initiate({ resourceId, cardKeys: [item.cardKey], priceTypeKey: key }, { subscribe: false, forceRefetch: true })).unwrap(),
        )
        return { kind: 'row', row: pickerRowOf(item, found.lines.get(item.cardKey)), product: found.products.get(item.cardKey) ?? null }
      } catch (error) {
        return { kind: 'failed', message: errorView(error).message }
      }
    },
    [dispatch, priceTypeKey, resourceId],
  )

  return useMemo(
    () => ({
      view: { search: vm, picker: { ...EMPTY_PICKER, search: searchText, rows: vm.rows }, pricesAtText: null },
      find: (productKey) => lines.products.get(productKey) ?? null,
      lookup,
      priceTypes,
      defaultPriceTypeKey: defaultKey,
    }),
    [defaultKey, lines.products, lookup, priceTypes, searchText, vm],
  )
}
